import React, { useEffect, useRef, useState } from 'react';

import { ScreenHead } from '../../ui/screen-parts.js';
import { Chip, Field, Panel, Empty, useFlash } from '../../ui/kit.js';
import { day, clock, nextId } from '../../state/building.js';

import Dinner from '../../media/images/modules/concierge-dinner.jpg';
import Car from '../../media/images/modules/concierge-car.jpg';
import Flowers from '../../media/images/modules/concierge-flowers.jpg';
import DryCleaning from '../../media/images/modules/concierge-dry-cleaning.jpg';
import Tickets from '../../media/images/modules/concierge-tickets.jpg';
import Desk from '../../media/images/modules/concierge-desk.jpg';

const STATUS_TONE = { 'Received': 'info', 'In progress': 'warn', 'Confirmed': 'ok' };

const QUICK = [
    { kind: 'Dinner reservation', detail: 'Table for 2 at Zuma Miami, 8:00 PM, window if possible', image: Dinner },
    { kind: 'Car service', detail: 'Black SUV to MIA, 2 passengers, 3 bags', image: Car },
    { kind: 'Flowers', detail: 'White orchid arrangement delivered to the unit, card to follow', image: Flowers },
    { kind: 'Dry cleaning pickup', detail: '3 suits and 5 shirts, hanging bag at the door', image: DryCleaning },
    { kind: 'Tickets', detail: '2 tickets for the Heat game Friday, lower bowl', image: Tickets },
    { kind: 'Other', detail: '', image: Desk }
];

const BLANK = { kind: 'Dinner reservation', detail: '', when: '' };

const SEED = [
    { id: 1, kind: 'Car service', detail: 'Sedan to Brickell City Centre', when: 'Tomorrow 10:00 AM', sent: day(-1), status: 'Confirmed' },
    { id: 2, kind: 'Flowers', detail: 'Peonies for a birthday, delivered to unit 1204', when: 'Saturday morning', sent: day(0), status: 'In progress' }
];

/* Concierge Requests: a tap prefills the request, the desk picks it up. */
function ConciergeScreen() {
    const [flash, notify] = useFlash();
    const [form, setForm] = useState(BLANK);
    const [requests, setRequests] = useState(SEED);
    const timers = useRef([]);

    useEffect(() => () => timers.current.forEach(clearTimeout), []);

    const set = key => event => setForm(f => ({ ...f, [key]: event.target.value }));

    const submit = event => {
        event.preventDefault();
        if (!form.detail.trim()) return notify('Tell the concierge what you need first.');

        const id = nextId();
        setRequests(list => [{ id, ...form, sent: `${day(0)} ${clock()}`, status: 'Received' }, ...list]);
        timers.current.push(setTimeout(() => {
            setRequests(list => list.map(r => (r.id === id ? { ...r, status: 'In progress' } : r)));
        }, 3000));

        setForm(BLANK);
        notify(`${form.kind} request sent. The concierge desk will confirm shortly.`);
    };

    return (
        <div className="screen-content">
            <ScreenHead eyebrow="Concierge" title="Concierge at your service" />
            {flash}

            <div className="tiles">
                {QUICK.map(q => (
                    <button
                        type="button"
                        className="tile tile--photo"
                        key={q.kind}
                        aria-pressed={form.kind === q.kind}
                        onClick={() => setForm({ kind: q.kind, detail: q.detail, when: '' })}
                    >
                        <img src={q.image} alt="" loading="lazy" />
                        <span>{q.kind}</span>
                    </button>
                ))}
            </div>

            <div className="split section-gap">
                <Panel as="form" className="panel--accent" onSubmit={submit}>
                    <h4>New request</h4>
                    <div className="form">
                        <Field label="Request type">
                            <select className="form-control" value={form.kind} onChange={set('kind')}>
                                {QUICK.map(q => <option key={q.kind}>{q.kind}</option>)}
                            </select>
                        </Field>
                        <Field label="When">
                            <input className="form-control" value={form.when} onChange={set('when')} placeholder="e.g. Tonight 7:30 PM" />
                        </Field>
                        <Field label="Details" wide>
                            <textarea className="form-control" rows="3" value={form.detail} onChange={set('detail')} placeholder="Names, times, preferences" />
                        </Field>
                        <button type="submit" className="btn btn-primary">Send to concierge</button>
                    </div>
                </Panel>

                <div className="stack">
                    <h4>Your requests</h4>
                    {requests.length === 0 ? <Empty>No requests yet.</Empty> : (
                        <ul className="rows">
                            {requests.map(r => (
                                <li className="row" key={r.id}>
                                    <div className="row__main">
                                        <span className="row__title">{r.kind}</span>
                                        <span className="row__meta">{r.detail}{r.when ? ` · ${r.when}` : ''} · sent {r.sent}</span>
                                    </div>
                                    <div className="row__end">
                                        <Chip tone={STATUS_TONE[r.status]}>{r.status}</Chip>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            </div>
        </div>
    );
}

export default ConciergeScreen;
